import chatService from './chatService';
import { forgetCachedChannel } from './chatHistoryCache';
import { useChatStore } from '../store/chatStore';
import { useNotificationStore } from '../store/notificationStore';

/**
 * The two things the chat sidebar's row context menu can do, kept out of the
 * component so the ORDER OF OPERATIONS is testable on its own.
 *
 * The order is the point. Leaving makes irreversible local changes — dropping a
 * conversation from the list, wiping this device's cached copy of its history —
 * and both actions mirror a server call that can fail. So the rule here,
 * uniformly:
 *
 *     await the server first; touch local state only after it resolved.
 *
 * A failure returns `{ ok: false }` and leaves the app exactly as it was, plus
 * an error toast. The opposite arrangement (act locally, fire the request,
 * shrug) is what makes a client that quietly disagrees with the server: the
 * conversation vanishes from the list, its history is gone from disk, and the
 * user is still a member — with nothing on screen to say so.
 *
 * Each returns `{ ok: boolean, error?: Error }` rather than throwing, because
 * every caller is a click handler whose only reaction to a throw would be to
 * swallow it.
 */

function errorMessage(e, fallback) {
  return e?.response?.data?.error || fallback;
}

/**
 * Mute or unmute a conversation. Deliberately NOT optimistic: the flag is a
 * server-owned field on the channel, so the UI shows the server's answer or
 * nothing. A muted-looking row that is not actually muted server-side is worse
 * than a click that visibly did nothing — the whole promise of mute is that the
 * silence holds on every device.
 *
 * API ASSUMPTION: the 200 carries the re-serialized channel. If the backend
 * answers without one (or omits `muted` on it), the call still SUCCEEDED, so
 * the flag is applied locally from the request instead — otherwise the menu
 * would keep offering "Mute" on a conversation that is already muted until the
 * next full `listChannels`.
 */
export async function setChannelMuted(channelId, muted) {
  if (!channelId) return { ok: false };
  try {
    const channel = await chatService.setMuted(channelId, muted);
    const patch = channel?.id ? { ...channel, muted: channel.muted ?? muted } : { id: channelId, muted };
    useChatStore.getState().upsertChannel(patch);
    return { ok: true };
  } catch (e) {
    useNotificationStore
      .getState()
      .error(errorMessage(e, muted ? 'Could not mute conversation' : 'Could not unmute conversation'));
    return { ok: false, error: e };
  }
}

/**
 * Leave a group/channel and purge this device's cached copy of it — the owner's
 * choice over leaving the history behind, and the same pair
 * `ChannelMembersPopover.leave` already performs. Leaving reuses the existing
 * member-removal endpoint with the user's own id; there is no dedicated leave
 * route.
 *
 * `removeChannel` drops every IN-MEMORY per-channel slice (buffer, unread,
 * presence, keys) and clears the active selection; the on-disk history is
 * separate state that it does not touch, hence `forgetCachedChannel`. The
 * server also broadcasts `channel_removed` to the leaver, and
 * `useChatRealtime` does the same two things from it — but that is a SOCKET
 * delivery for a REST call that has already succeeded, so if the socket is down
 * at this moment nothing ever retries and the history keeps rendering from
 * cache on the next launch. Both paths are idempotent.
 */
/**
 * Delete an orphaned DM — the conversation, its messages and every file
 * uploaded to it.
 *
 * Mirrors leaveChannel's order for the same reason: nothing local happens
 * until the SERVER has accepted. Removing the row first and then failing the
 * request would hide a conversation that still exists, with no way back to it
 * from the UI.
 */
export async function deleteOrphanedDm(channelId) {
  if (!channelId) return { ok: false };
  try {
    await chatService.deleteChannel(channelId);
  } catch (e) {
    useNotificationStore.getState().error(errorMessage(e, 'Could not delete the conversation'));
    return { ok: false, error: e };
  }
  useChatStore.getState().removeChannel(channelId);
  // The local cache holds the same messages the server just destroyed; leaving
  // it would keep the conversation readable on this device forever.
  await forgetCachedChannel(channelId);
  return { ok: true };
}

export async function leaveChannel(channelId, userId) {
  if (!channelId || userId == null) return { ok: false };
  try {
    await chatService.removeMember(channelId, userId);
  } catch (e) {
    useNotificationStore.getState().error(errorMessage(e, 'Could not leave channel'));
    return { ok: false, error: e };
  }
  useChatStore.getState().removeChannel(channelId);
  await forgetCachedChannel(channelId);
  return { ok: true };
}
